<template><div ref="$container" class="confirm-box"></div></template>
<script lang="ts">
/* eslint-disable no-underscore-dangle,no-unused-expressions */
import { useI18n } from 'vue-i18n'
import { createVNode, isRef, Ref, render } from 'vue'
import { DialogProps, ElButton, ElDialog } from 'element-plus'

import event from '@/utils/event'

export enum CONFIRM_EVENT_NAMES {
  SHOW = 'CONFIRM_EVENT_NAMES_SHOW',
  HIDE = 'CONFIRM_EVENT_NAMES_HIDE',
}

export type ConfirmConfig = {
  __id: string
  // 弹窗的宽度
  width?: number
  // Confirm 弹出框的标题，必填
  title: Ref<string> | string
  // Confirm 弹出框的内容，目前支持字符串、JSX，必填
  content: ((render?: typeof h) => JSX.Element) | string | JSX.Element | JSX.Element[]
  // DialogProps 透传 ElDialog 的属性值（除 modelValue 和 onClosed）
  dialogProps?: Partial<Omit<DialogProps, 'modelValue' | 'onClosed'>>
  extraButtons?: (onCancel?: () => void) => JSX.Element | JSX.Element[] | null
  // Cancel 按钮的文案，如果为 null 则不显示
  cancelText?: string | null
  // Confirm 按钮的文案，如果为 null 则不显示
  confirmText?: string | null
  // Confirm 按钮禁用
  confirmDisabled?: Ref<boolean>
  // Cancel 按钮禁用
  cancelDisabled?: Ref<boolean>
  // 点击 confirm 按钮时，是否需要 loading
  withConfirmLoading?: boolean
  // 点击 confirm 按钮时的回调
  onConfirm?: () => void
  // 点击 cancel 按钮时的回调
  onCancel?: () => void
  // 点击 close 按钮时的回调
  onClose?: () => void
  // 点击 cancel 之前执行
  // 如果返回值为 true，则会继续执行 onCancel 函数，如果返回值为 false，则会暂停当前的 confirm 关闭
  // 若想要手动关闭，请执行 show 返回的函数
  onBeforeClose?: () => boolean
}

export type Handler = (config: ConfirmConfig) => void

export default defineComponent({
  setup() {
    const { t } = useI18n()
    const confirms = reactive({})
    const $container = ref<HTMLDivElement>()

    const handler: Handler = ({
      __id,
      title,
      width,
      content,
      cancelText,
      confirmText,
      dialogProps,
      extraButtons,
      confirmDisabled = ref<boolean>(false),
      cancelDisabled = ref<boolean>(false),
      withConfirmLoading,
      onConfirm,
      onCancel,
      onClose,
      onBeforeClose,
    }) => {
      confirms[__id] = {
        __id,
        title,
        content,
        cancelText,
        confirmText,
        visible: true,
        loading: false,
        onCancel,
        onConfirm,
        onClose,
        onBeforeClose,
      }
      const container = document.createElement('div')

      if (dialogProps?.draggable === true)
        container.classList.add('confirm-box__wrapper--draggable')

      const confirmVNode = createVNode(
        ElDialog,
        {
          width: `${width ?? 400}px`,
          'close-on-click-modal': false,
          ...dialogProps,
          modelValue: true,
          onClosed: () => {
            if (confirms[__id]) {
              confirms[__id]?.onClose?.()
              confirms[__id].__container.remove()
              confirms[__id].__vNode = null
              delete confirms[__id]
            }
          },
        },
        {
          header: () => (isRef(title) ? title.value : title),
          default: typeof content === 'function' ? content : () => content,
          footer:
            cancelText === null && confirmText === null && !extraButtons
              ? null
              : () =>
                  h('div', { class: 'flex justify-end items-center' }, [
                    ...(cancelText === null
                      ? []
                      : [
                          h(
                            ElButton,
                            {
                              disabled: cancelDisabled.value,
                              onClick: () => {
                                if (confirms[__id]) {
                                  const closable = confirms[__id]?.onBeforeClose?.() ?? true
                                  if (!closable) return false
                                  confirms[__id]?.onCancel?.()
                                }
                                confirmVNode.component!.exposed!.visible.value = false
                              },
                            },
                            () => cancelText ?? t('common.button.cancel')
                          ),
                        ]),
                    ...(confirmText === null
                      ? [extraButtons?.(confirms[__id]?.onCancel)]
                      : [
                          extraButtons?.(confirms[__id]?.onCancel),
                          h(
                            ElButton,
                            {
                              type: 'primary',
                              disabled: confirmDisabled.value,
                              ...(withConfirmLoading ? { loading: confirms?.[__id]?.loading } : {}),
                              onClick: async () => {
                                try {
                                  confirms[__id] && (confirms[__id].loading = true)
                                  await onConfirm?.()
                                  confirmVNode.component!.exposed!.visible.value = false
                                } catch (error) {
                                  console.log('Confirm onConfirm =>', error)
                                } finally {
                                  confirms[__id] && (confirms[__id].loading = false)
                                }
                              },
                            },
                            () => confirmText ?? t('common.button.confirm')
                          ),
                        ]),
                  ]),
        }
      )
      render(confirmVNode, container)

      confirms[__id].__vNode = confirmVNode
      confirms[__id].__container = container

      if ($container) {
        $container.value?.appendChild?.(container)
      }
    }

    const handler4hide = ({ __id: id }: { __id: string }) => {
      if (confirms[id]) {
        confirms[id]?.__container.remove()
        confirms[id].__vNode = null
        delete confirms[id]
      }
    }

    onMounted(() => {
      event.subscribe(CONFIRM_EVENT_NAMES.SHOW, handler)
      event.subscribe(CONFIRM_EVENT_NAMES.HIDE, handler4hide)
    })

    onUnmounted(() => {
      event.unsubscribe(CONFIRM_EVENT_NAMES.SHOW)
      event.unsubscribe(CONFIRM_EVENT_NAMES.HIDE)
    })

    return {
      $container,
    }
  },
})
</script>

<style lang="scss" scoped></style>
